import { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getCountryOptions } from '../lib/countries';
import './PassportPage.css';

/**
 * ゲスト向けパスポート登録ページ（多言語 ja/en/zh）
 *   - 自社予約: /passport/:token （トークンで予約特定）
 *   - OTA予約 : /passport     （固定URL。予約番号+チェックイン日で本人確認 → トークン取得）
 *   画面1: 予約確認 / 画面2: ゲスト一覧 / 画面3: 情報入力＋画像アップロード
 */

interface GuestSlot {
  id: string;
  seq_no: number;
  status: string;
  display_name: string;
  registered: boolean;
}
interface VerifyResult {
  reservation_id: string;
  property_name: string;
  checkin_date: string;
  checkout_date: string;
  guest_count: number;
  registered_count: number;
  guests: GuestSlot[];
}

type Step = 'verify' | 'list' | 'edit' | 'done';

const ACCEPT = '.jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf';
const ACCEPT_MIME = ['image/jpeg', 'image/png', 'application/pdf'];
const ACCEPT_EXT = ['.jpg', '.jpeg', '.png', '.pdf'];

// ファイルが許可された形式か（MIMEタイプ or 拡張子で判定）。
// ドラッグ&ドロップではMIMEが空のことがあるため拡張子でもフォールバック判定する。
function isAcceptedFile(f: File): boolean {
  if (f.type && ACCEPT_MIME.includes(f.type)) return true;
  const lower = f.name.toLowerCase();
  return ACCEPT_EXT.some((ext) => lower.endsWith(ext));
}

const LANGS: { code: string; label: string }[] = [
  { code: 'ja', label: '日本語' }, { code: 'en', label: 'English' }, { code: 'zh', label: '中文' },
];
// 画面上部の主要3言語はボタン、その他の言語（仏・西・韓）は右側のドロップダウンで提供する。
const LANGS_MORE: { code: string; label: string }[] = [
  { code: 'fr', label: 'Français' }, { code: 'es', label: 'Español' }, { code: 'ko', label: '한국어' },
];

export default function PassportPage() {
  const { t, i18n } = useTranslation();
  const params = useParams();
  const urlToken = params.token || '';
  // OTA予約の固定URLに ?code=XXXX が付いている場合、予約番号欄へ初期入力する。
  // 自動照会まではせず、ゲストがチェックイン日などを確認して「確認」ボタンを押す（手動確認）。
  const [searchParams] = useSearchParams();
  const prefillCode = (searchParams.get('code') || '').trim();
  // 入室後チェックインは OTA/自社を区別せず、全予約「予約番号＋チェックイン日」で統一照会する。
  // （全予約に access_code を発番済み。/ota-verify は access_code 照合でトークンを発行する）

  // 国籍セレクト用の国リスト（現在の言語に応じて国名を表示。保存値は国コード）。
  const uiLang = ['ja', 'en', 'zh'].includes(i18n.language) ? i18n.language : 'en';
  const countryOptions = getCountryOptions(uiLang);

  const [token, setToken] = useState(urlToken);
  const [step, setStep] = useState<Step>('verify');
  const [accessCodeInput, setAccessCodeInput] = useState(prefillCode);
  const [data, setData] = useState<VerifyResult | null>(null);
  const [guests, setGuests] = useState<GuestSlot[]>([]);
  const [activeGuest, setActiveGuest] = useState<GuestSlot | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // 自社予約: URLにトークンがある場合、予約番号入力を挟まず自動で予約確認する。
  // その自動確認が進行中かどうか（完了するまで入口フォームを出さない）。
  const [tokenChecking, setTokenChecking] = useState(!!urlToken);

  const [form, setForm] = useState({ name: '', birthdate: '', gender: '', nationality: '', passport_number: '', address: '', prev_accommodation: '', next_destination: '' });
  // 日本国籍の場合はパスポート画像・パスポート番号を任意にする（旅館業法上、国内在住日本人は旅券呈示義務の対象外）。
  // 判定は保存値（国コード）で行う。countries.ts の日本は 'JP'。
  const isJapanese = form.nationality === 'JP';
  const [file, setFile] = useState<File | null>(null);
  const [faceFile, setFaceFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const faceInputRef = useRef<HTMLInputElement>(null);
  // 施設タブレットからのアクセスか（?source=tablet）。正規提出の記録に使う（自己申告扱い）。
  const submitSource = (new URLSearchParams(window.location.search).get('source') === 'tablet') ? 'tablet' : 'guest_device';

  // ファイル選択（input / ドロップ共通）。形式チェックして state に反映。
  function selectFile(f: File | null | undefined) {
    if (!f) return;
    if (!isAcceptedFile(f)) {
      setFile(null);
      setError(t('passport.errUnsupported'));
      return;
    }
    setError('');
    setFile(f);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    selectFile(e.dataTransfer.files?.[0]);
  }

  const refreshGuests = useCallback(async (tk: string) => {
    const res = await fetch(`/api/passport/guests?token=${encodeURIComponent(tk)}`);
    const d = await res.json().catch(() => ({}));
    let allRegistered = false;
    if (res.ok) {
      setGuests(d.guests || []);
      allRegistered = !!d.all_registered;
      if (allRegistered) setStep('done');
    }
    return allRegistered;
  }, []);

  function applyVerifyResult(d: VerifyResult) {
    setData(d);
    setGuests(d.guests || []);
    if (d.guests?.length && d.guests.every((g) => g.registered)) setStep('done');
    else setStep('list');
  }

  // 自社予約: URLにトークンがあれば、読み込み時にトークンで予約を確認する（予約番号入力は不要）。
  // ただし一覧へは自動遷移せず、確認画面で「確認して進む」ボタンを押させる。
  // 失敗時（無効/期限切れ等）はエラーを表示する。
  useEffect(() => {
    if (!urlToken) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/passport/verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: urlToken }),
        });
        const d = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          const map: Record<string, string> = {
            expired: 'errOtaExpired', revoked: 'errOtaExpired',
            not_found: 'errOtaInvalid', cancelled: 'errCancelled',
          };
          setError(t(`passport.${map[d.reason] || 'errVerify'}`));
          return;
        }
        // 確認画面にとどめる（自動で一覧へ進めない）。データだけ保持し、
        // ゲストが「確認して進む」ボタンを押したら applyVerifyResult で遷移する。
        setToken(urlToken);
        setData(d);
        setGuests(d.guests || []);
      } catch {
        if (!cancelled) setError(t('passport.errVerify'));
      } finally {
        if (!cancelled) setTokenChecking(false);
      }
    })();
    return () => { cancelled = true; };
    // urlToken は初回マウント時に確定。再実行不要。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 自社予約: 確認画面でボタンを押したときに一覧（または完了）へ進む。
  function confirmAndProceed() {
    if (data) applyVerifyResult(data);
  }


  async function handleVerifyOta(e: React.FormEvent) {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      const res = await fetch('/api/passport/ota-verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_code: accessCodeInput }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        const map: Record<string, string> = {
          invalid: 'errOtaInvalid', cancelled: 'errCancelled',
          expired: 'errOtaExpired', rate_limited: 'errRateLimited',
        };
        setError(t(`passport.${map[d.reason] || 'errVerify'}`));
        return;
      }
      setToken(d.token);
      applyVerifyResult(d);
    } finally { setBusy(false); }
  }

  function openEdit(g: GuestSlot) {
    setActiveGuest(g);
    setForm({ name: '', birthdate: '', gender: '', nationality: '', passport_number: '', address: '', prev_accommodation: '', next_destination: '' });
    setFile(null);
    setFaceFile(null);
    setDragOver(false);
    setError('');
    setStep('edit');
  }

  // 「ゲストを追加」: 事前登録のない同伴者の枠を新規作成し、その枠の入力フォームを開く。
  //   バックエンド POST /api/passport/guests が新しい guest 枠を作り、予約人数を +1 する。
  async function addGuest() {
    setError(''); setBusy(true);
    try {
      const res = await fetch(`/api/passport/guests?token=${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.guest) { setError(t('passport.errAddGuest')); return; }
      await refreshGuests(token);
      // 追加直後の枠に対して情報入力フォームを開く
      openEdit(d.guest as GuestSlot);
    } catch {
      setError(t('passport.errAddGuest'));
    } finally { setBusy(false); }
  }

  async function handleSubmitGuest(e: React.FormEvent) {
    e.preventDefault();
    if (!activeGuest) return;
    setError(''); setBusy(true);
    try {
      const r1 = await fetch(`/api/passport/guests/${activeGuest.id}?token=${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const d1 = await r1.json().catch(() => ({}));
      if (!r1.ok) { setError(t('passport.errInfoSave')); return; }

      // 日本国籍はパスポート画像を任意とする。それ以外は従来どおり必須。
      if (!isJapanese && !file) { setError(t('passport.errFileRequired')); return; }

      // 顔画像 → kind=face、旅券画像 → kind=passport。両方とも source を付けて提出元を記録。
      const uploadOne = async (f: File, kind: 'face' | 'passport') => {
        const fd = new FormData();
        fd.append('file', f);
        const q = `token=${encodeURIComponent(token)}&kind=${kind}&source=${submitSource}`;
        const r = await fetch(`/api/passport/guests/${activeGuest.id}/file?${q}`, { method: 'POST', body: fd });
        const d = await r.json().catch(() => ({}));
        return { ok: r.ok, d };
      };

      // 顔写真は任意（事前登録ではアップロードされていれば送信。チェックイン時に必須）。
      if (faceFile) {
        const face = await uploadOne(faceFile, 'face');
        if (!face.ok) {
          const map: Record<string, string> = { too_large: 'errTooLarge', unsupported_type: 'errUnsupported', heic_conversion_pending: 'errHeic', storage_failed: 'errStorage' };
          setError(t(`passport.${map[face.d.reason || face.d.error] || 'errUpload'}`));
          return;
        }
      }
      // 旅券画像がある場合のみアップロード（日本国籍で未選択のときはスキップ）。
      if (file) {
        const pass = await uploadOne(file, 'passport');
        if (!pass.ok) {
          const map: Record<string, string> = { too_large: 'errTooLarge', unsupported_type: 'errUnsupported', heic_conversion_pending: 'errHeic', storage_failed: 'errStorage' };
          setError(t(`passport.${map[pass.d.reason || pass.d.error] || 'errUpload'}`));
          return;
        }
      }
      // 完了判定は refreshGuests の戻り値（all_registered）で行う。
      // 画像をスキップした日本国籍ケースでも、サーバー側の登録状況で正しく判定できる。
      const done = await refreshGuests(token);
      setStep(done ? 'done' : 'list');
    } finally { setBusy(false); }
  }

  const LangSwitcher = () => (
    <div className="pp-lang">
      <div className="pp-lang-btns">
        {LANGS.map((l) => (
          <button key={l.code} type="button"
            className={`pp-lang-btn ${i18n.language === l.code ? 'active' : ''}`}
            onClick={() => i18n.changeLanguage(l.code)}>{l.label}</button>
        ))}
      </div>
      <select
        className="pp-lang-more"
        aria-label="Language"
        value={LANGS_MORE.some((l) => l.code === i18n.language) ? i18n.language : ''}
        onChange={(e) => { if (e.target.value) i18n.changeLanguage(e.target.value); }}
      >
        <option value="">🌐 Other</option>
        {LANGS_MORE.map((l) => (
          <option key={l.code} value={l.code}>{l.label}</option>
        ))}
      </select>
    </div>
  );

  // ====== レンダリング ======
  if (step === 'verify') {
    return (
      <div className="pp-wrap">
        <LangSwitcher />
        <h1 className="pp-title">{t('passport.title')}</h1>
        <div className="pp-legal-notice">
          <p>{t('passport.legalNotice1')}</p>
          <p className="pp-legal-emphasis">{t('passport.legalNotice2')}</p>
          <p className="pp-legal-emphasis">{t('passport.legalNotice2b')}</p>
          <p>{t('passport.legalNotice3')}</p>
        </div>
        {urlToken ? (
          // 自社予約: 読み込み時にトークンで予約確認。確認後は「確認して進む」ボタンで一覧へ。
          <div className="pp-card">
            {tokenChecking ? (
              <p className="pp-sub">{t('passport.verifying')}</p>
            ) : error ? (
              <p className="pp-error">{error}</p>
            ) : (
              <>
                {data?.property_name && <p className="pp-meta">{data.property_name}</p>}
                {data && (
                  <dl className="pp-summary">
                    <div><dt>{t('passport.sumCheckin')}</dt><dd>{data.checkin_date}</dd></div>
                    <div><dt>{t('passport.sumCheckout')}</dt><dd>{data.checkout_date}</dd></div>
                    <div><dt>{t('passport.sumGuests')}</dt><dd>{data.guest_count}{t('passport.sumGuestsUnit')}</dd></div>
                  </dl>
                )}
                <button type="button" className="pp-btn" onClick={confirmAndProceed}>
                  {t('passport.confirmStart')}
                </button>
              </>
            )}
          </div>
        ) : (
          // OTA予約: 共通URLのため予約番号で本人確認する。
          <>
            <p className="pp-sub">{t('passport.subOta')}</p>
            <form onSubmit={handleVerifyOta} className="pp-card">
              <label className="pp-label">{t('passport.bookingNumber')}
                <input type="text" value={accessCodeInput} required autoCapitalize="characters"
                  onChange={(e) => setAccessCodeInput(e.target.value.toUpperCase())} className="pp-input"
                  placeholder={t('passport.bookingNumberPlaceholder')} />
              </label>
              {error && <p className="pp-error">{error}</p>}
              <button type="submit" className="pp-btn" disabled={busy}>{busy ? t('passport.verifying') : t('passport.verify')}</button>
            </form>
          </>
        )}
        {/* プライバシー保護の趣旨＋ポリシーリンク（入口画面の下半分） */}
        <div className="pp-privacy-notice">
          <div className="pp-privacy-head">
            <span className="pp-privacy-icon" aria-hidden="true">🔒</span>
            <span className="pp-privacy-title">{t('passport.privacyHeading')}</span>
          </div>
          <p className="pp-privacy-body">{t('passport.privacyNotice')}</p>
          <p className="pp-privacy-link">
            {t('passport.privacyLinkPrefix')}
            <Link to="/privacy" target="_blank" rel="noopener noreferrer">{t('passport.privacyLinkText')}</Link>
            {t('passport.privacyLinkSuffix')}
          </p>
        </div>
      </div>
    );
  }

  if (step === 'list' && data) {
    const regCount = guests.filter((g) => g.registered).length;
    return (
      <div className="pp-wrap">
        <LangSwitcher />
        <h1 className="pp-title">{t('passport.listTitle')}</h1>
        <p className="pp-progress">{t('passport.progress', { done: regCount, total: data.guest_count })}</p>
        <p className="pp-meta">{data.property_name}</p>
        <ul className="pp-guest-list">
          {guests.map((g) => (
            <li key={g.id} className={`pp-guest ${g.registered ? 'done' : ''}`}>
              <div>
                <strong>{t('passport.guest', { n: g.seq_no })}{g.registered ? `：${g.display_name}` : ''}</strong>
                <span className="pp-status">{g.registered ? t('passport.registered') : t('passport.unregistered')}</span>
              </div>
              <button className="pp-btn-sm" onClick={() => openEdit(g)}>
                {g.registered ? t('passport.reRegister') : t('passport.register')}
              </button>
            </li>
          ))}
        </ul>
        <button type="button" className="pp-btn-add" onClick={addGuest} disabled={busy}>
          ＋ {busy ? t('passport.adding') : t('passport.addGuest')}
        </button>
      </div>
    );
  }

  if (step === 'edit' && activeGuest) {
    return (
      <div className="pp-wrap">
        <LangSwitcher />
        <h1 className="pp-title">{t('passport.editTitle', { n: activeGuest.seq_no })}</h1>
        <form onSubmit={handleSubmitGuest} className="pp-card">
          <p className="pp-usage-note">{t('passport.usageNote')}</p>
          <div className="pp-form-grid">
          <label className="pp-label">{t('passport.name')}
            <input className="pp-input" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </label>
          <label className="pp-label">{t('passport.birthdate')}
            <input className="pp-input" type="date" required value={form.birthdate}
              onChange={(e) => setForm({ ...form, birthdate: e.target.value })} />
          </label>
          <label className="pp-label">{t('passport.gender')}
            <select className="pp-input" required value={form.gender}
              onChange={(e) => setForm({ ...form, gender: e.target.value })}>
              <option value="">{t('passport.genderSelect')}</option>
              <option value="male">{t('passport.male')}</option>
              <option value="female">{t('passport.female')}</option>
              <option value="other">{t('passport.other')}</option>
            </select>
          </label>
          <label className="pp-label">{t('passport.nationality')}
            <select className="pp-input" required value={form.nationality}
              onChange={(e) => setForm({ ...form, nationality: e.target.value })}>
              <option value="">{t('passport.nationalitySelect')}</option>
              {/* 旧データ（国コードに一致しない自由入力値）が入っていた場合も失わず表示する */}
              {form.nationality &&
                !countryOptions.some((c) => c.code === form.nationality) && (
                  <option value={form.nationality}>{form.nationality}</option>
                )}
              {countryOptions.map((c) => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </select>
          </label>
          <label className="pp-label">{t('passport.passportNumber')}
            <input className="pp-input" required={!isJapanese} value={form.passport_number}
              onChange={(e) => setForm({ ...form, passport_number: e.target.value })} />
          </label>
          {isJapanese && (
            <p className="pp-field-hint pp-col-full">{t('passport.jpNoPassportNote')}</p>
          )}
          <label className="pp-label pp-col-full">{t('passport.address')}
            <input className="pp-input" required value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </label>
          <label className="pp-label pp-col-full">{t('passport.prevAccommodation')}
            <input className="pp-input" required value={form.prev_accommodation}
              placeholder={t('passport.prevAccommodationPlaceholder')}
              onChange={(e) => setForm({ ...form, prev_accommodation: e.target.value })} />
          </label>
          <label className="pp-label pp-col-full">{t('passport.nextDestination')}
            <input className="pp-input" required value={form.next_destination}
              placeholder={t('passport.nextDestinationPlaceholder')}
              onChange={(e) => setForm({ ...form, next_destination: e.target.value })} />
          </label>
          <div className="pp-label pp-col-full">{t('passport.faceFileOptional')}
            <p className="pp-field-hint">{t('passport.faceOptionalNote')}</p>
            <div
              className={`pp-dropzone ${faceFile ? 'has-file' : ''}`}
              onClick={() => faceInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); faceInputRef.current?.click(); } }}
            >
              <input ref={faceInputRef} className="pp-dropzone-input" type="file" accept={ACCEPT}
                onChange={(e) => { const f = e.target.files?.[0]; if (f && isAcceptedFile(f)) { setError(''); setFaceFile(f); } else if (f) { setFaceFile(null); setError(t('passport.errUnsupported')); } }} />
              {faceFile ? (
                <div className="pp-dropzone-file">
                  <span className="pp-dropzone-filename">{faceFile.name}</span>
                  <button type="button" className="pp-dropzone-clear"
                    onClick={(e) => { e.stopPropagation(); setFaceFile(null); if (faceInputRef.current) faceInputRef.current.value = ''; }}>
                    {t('passport.dropRemove')}
                  </button>
                </div>
              ) : (
                <div className="pp-dropzone-prompt">
                  <span className="pp-dropzone-main">{t('passport.faceDropHint')}</span>
                  <span className="pp-dropzone-sub">{t('passport.faceDropSub')}</span>
                </div>
              )}
            </div>
            <p className="pp-field-note">{t('passport.faceAutoDeleteNote')}</p>
          </div>
          <div className="pp-label pp-col-full">{t('passport.file')}
            <div
              className={`pp-dropzone ${dragOver ? 'dragover' : ''} ${file ? 'has-file' : ''}`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
              onDrop={handleDrop}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click(); } }}
            >
              <input ref={fileInputRef} className="pp-dropzone-input" type="file" accept={ACCEPT}
                onChange={(e) => selectFile(e.target.files?.[0])} />
              {file ? (
                <div className="pp-dropzone-file">
                  <span className="pp-dropzone-filename">{file.name}</span>
                  <button type="button" className="pp-dropzone-clear"
                    onClick={(e) => { e.stopPropagation(); setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
                    {t('passport.dropRemove')}
                  </button>
                </div>
              ) : (
                <div className="pp-dropzone-prompt">
                  <span className="pp-dropzone-main">{t('passport.dropHint')}</span>
                  <span className="pp-dropzone-sub">{t('passport.dropOr')}</span>
                </div>
              )}
            </div>
            <p className="pp-field-note">{t('passport.residentIdNote')}</p>
          </div>
          </div>
          {error && <p className="pp-error">{error}</p>}
          <div className="pp-actions">
            <button type="button" className="pp-btn-ghost" onClick={() => setStep('list')} disabled={busy}>{t('passport.back')}</button>
            <button type="submit" className="pp-btn" disabled={busy}>{busy ? t('passport.submitting') : t('passport.submit')}</button>
          </div>
        </form>
      </div>
    );
  }

  // done
  return (
    <div className="pp-wrap">
      <LangSwitcher />
      <h1 className="pp-title">{t('passport.doneTitle')}</h1>
      <p className="pp-sub">{t('passport.doneBody')}</p>
    </div>
  );
}
